/**
 * Auth service — replaces the GoTrue client with direct calls to our
 * self-hosted NestJS auth endpoints at /api/auth/*.
 *
 * Token storage key: 'seedhub-auth'
 * Shape stored: { accessToken, expiresAt }
 *
 * The refresh token is held by the server in a HttpOnly Secure cookie
 * (`rg_refresh`, scoped to /api/auth, SameSite=Strict). It never enters
 * JS storage, so a same-origin XSS cannot exfiltrate it.
 */

const STORAGE_KEY = 'seedhub-auth'
// See the note in lib/api-client.ts: relative, so the dev proxy and the
// same-origin production build both resolve without any environment file.
const API_URL = (import.meta as any).env?.VITE_API_URL || '/api'

/** How long to wait for another tab's rotation to land before giving up. */
const RETRY_DELAY_MS = 250

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  fullName: string | null
  phone: string | null
  jobTitle: string | null
  emailVerified: boolean
  organizationId: string | null
}

export interface AuthSession {
  accessToken: string
  /** Unix timestamp (ms) when the access token expires */
  expiresAt: number
  user: AuthUser
}

interface StoredTokens {
  accessToken: string
  expiresAt: number
}

// ── Storage helpers ────────────────────────────────────────────────────────────

function loadTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredTokens> & { refreshToken?: string }
    if (!parsed.accessToken || typeof parsed.expiresAt !== 'number') return null
    // Strip any legacy refreshToken field that was persisted by older builds.
    if (parsed.refreshToken) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ accessToken: parsed.accessToken, expiresAt: parsed.expiresAt }))
      } catch {}
    }
    return { accessToken: parsed.accessToken, expiresAt: parsed.expiresAt }
  } catch {
    return null
  }
}

function saveTokens(tokens: StoredTokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
}

function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Decode the `exp` claim from a JWT without verifying the signature.
 * The server still validates the signature — this is only used client-side to
 * decide when to proactively refresh before the token expires.
 */
function decodeExp(token: string): number {
  try {
    const payload = token.split('.')[1]
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return decoded.exp ? decoded.exp * 1000 : Date.now() + 60 * 60 * 1000
  } catch {
    return Date.now() + 60 * 60 * 1000
  }
}

/**
 * Decode the `sub` claim, again without verifying the signature.
 *
 * Only used to tell "another tab rotated my token" from "another tab signed
 * somebody else in", which decides whether this tab needs to re-fetch the
 * profile. It grants nothing, so an unverifiable token answering `null` is a
 * safe outcome rather than a hole.
 */
function decodeSubject(token: string): string | null {
  try {
    const payload = token.split('.')[1]
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof decoded.sub === 'string' ? decoded.sub : null
  } catch {
    return null
  }
}

// ── HTTP helpers ───────────────────────────────────────────────────────────────

async function post<T>(path: string, body: unknown, authToken?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`

  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
  })

  const text = await res.text()
  let data: any
  try {
    data = JSON.parse(text)
  } catch {
    data = { message: text }
  }

  if (!res.ok) {
    // The status rides along on the error. Callers have to tell "the server
    // refused this credential" from "the server did not answer about it", and
    // the message alone cannot carry that.
    const error = new Error(
      data?.message || data?.error || `Request failed (${res.status})`,
    ) as Error & { status?: number }
    error.status = res.status
    throw error
  }

  return data as T
}

async function get<T>(path: string, authToken: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    credentials: 'include',
  })

  const text = await res.text()
  let data: any
  try {
    data = JSON.parse(text)
  } catch {
    data = { message: text }
  }

  if (!res.ok) {
    throw new Error(data?.message || data?.error || `Request failed (${res.status})`)
  }

  return data as T
}

// ── Auth state change subscriptions ───────────────────────────────────────────

type AuthEvent = 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED'
type AuthStateCallback = (event: AuthEvent, session: AuthSession | null) => void

const subscribers = new Set<AuthStateCallback>()

function notify(event: AuthEvent, session: AuthSession | null) {
  subscribers.forEach((cb) => {
    try {
      cb(event, session)
    } catch {
      // Subscriber errors must not crash the auth system
    }
  })
}

// ── Cross-tab session sync ────────────────────────────────────────────────────

/**
 * Tell the other tabs of this origin that the stored session changed.
 *
 * `localStorage` is shared by every tab; the subscriber list above is not. So a
 * sign-out emptied the store for every tab and told only its own, and the other
 * tabs went on holding a populated `user` in React state over an empty store.
 * The two route guards that read those two things then disagreed permanently:
 * `DashboardShell` reads the store, saw no session and sent the visitor to
 * /auth; the sign-in page read React state, saw a user and sent them back to
 * /dashboard. Both redirect with `replace`, so there was not even a history
 * entry to escape through, and the tab thrashed between the two routes at
 * roughly eight hundred navigations a second until the renderer died. Two tabs
 * open is ordinary use and signing out is the commonest way a session ends, so
 * this was reachable by simply using the product.
 *
 * The browser's own `storage` event is the missing half of the notification: it
 * fires in every OTHER tab of the origin when one of them writes, and never in
 * the tab that did the writing. Feeding it into `notify` makes React state in
 * every tab follow the store, which is what stops the guards contradicting each
 * other in the first place. `Auth.tsx` additionally requires the stored session
 * before it will redirect, so even a browser that withheld the event could not
 * reopen the cycle.
 *
 * Anything else that empties the store gets the same treatment: a browser
 * clearing site data, a profile sync, devtools.
 */
function handleSessionStorageEvent(e: StorageEvent) {
  // `key === null` is a whole-store `clear()`, which takes our key with it.
  if (e.key !== null && e.key !== STORAGE_KEY) return

  const tokens = loadTokens()
  if (!tokens) {
    notify('SIGNED_OUT', null)
    return
  }

  // A token is present, so the other tab signed in or rotated its access token.
  // Which of the two matters: a rotation leaves the same person signed in and
  // only the token is stale, while a sign-in as somebody else leaves this tab
  // showing the previous person's name over the new person's credentials. The
  // `sub` claim separates them, and it costs one base64 decode.
  //
  // `user` is deliberately not persisted, so subscribers re-hydrate the profile
  // from /auth/me when they are told SIGNED_IN.
  const previousSubject = e.oldValue ? subjectOfStoredValue(e.oldValue) : null
  const nextSubject = decodeSubject(tokens.accessToken)

  /**
   * TOKEN_REFRESHED is claimed only when both subjects are readable AND equal.
   *
   * It used to be the fall-through for everything else, which made the ordinary
   * sequence "sign out in tab A, then sign in as somebody else" arrive in every
   * other tab as a token refresh. `oldValue` is null for a `setItem` on a key
   * that does not currently exist, and signing out removes the key, so
   * `previousSubject` was null on precisely the transition that matters and the
   * classifier could not see the identity change at all.
   *
   * Same person is the claim that needs proof. Anything less than proof is
   * treated as a new identity, which costs one cache clear and one /auth/me on
   * an ambiguous refresh, and buys a guarantee that a second person's session
   * never inherits the first person's cached tenant data.
   */
  const sameSubject = !!previousSubject && !!nextSubject && previousSubject === nextSubject
  const event: AuthEvent = sameSubject ? 'TOKEN_REFRESHED' : 'SIGNED_IN'

  notify(event, { ...tokens, user: null as unknown as AuthUser })
}

/**
 * Whose access token is this?
 *
 * Exported because the cross-tab notification above carries `user: null` by
 * construction (the user object is deliberately never persisted), so a
 * subscriber that has to know WHO the new session belongs to before it can
 * safely reuse anything has only the token to read it from. See `adoptIdentity`
 * in useAuth.
 */
export function subjectOfAccessToken(accessToken: string | null | undefined): string | null {
  if (!accessToken) return null
  return decodeSubject(accessToken)
}

function subjectOfStoredValue(raw: string): string | null {
  try {
    const parsed = JSON.parse(raw) as { accessToken?: string }
    return parsed.accessToken ? decodeSubject(parsed.accessToken) : null
  } catch {
    return null
  }
}

if (typeof window !== 'undefined') {
  // Removed first so a hot module replacement in development cannot leave two
  // copies of the listener attached to the same window.
  window.removeEventListener('storage', handleSessionStorageEvent)
  window.addEventListener('storage', handleSessionStorageEvent)
}

// ── In-flight refresh deduplication ───────────────────────────────────────────

let refreshPromise: Promise<StoredTokens> | null = null

async function doRefresh(): Promise<StoredTokens> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const data = await post<{ accessToken: string }>('/auth/refresh', {})
      const tokens: StoredTokens = {
        accessToken: data.accessToken,
        expiresAt: decodeExp(data.accessToken),
      }
      saveTokens(tokens)
      return tokens
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Why a refresh failed, which is the whole question.
 *
 * `expired` means the server refused the refresh token itself: it is spent,
 * revoked, or the session behind it has ended. There is nothing to recover and
 * the user has to sign in.
 *
 * `unavailable` means we never got an answer about the token. A rate limit, a
 * network blip, a 500, a proxy. The session may be perfectly fine, and throwing
 * the user out on that evidence is how a 429 came to be indistinguishable from a
 * revoked token: ten refreshes a minute is a per-IP limit, so a practice behind
 * one NAT address signed out its eleventh person to open the app, and then
 * everybody after them.
 */
export type RefreshOutcome =
  | { status: 'refreshed'; accessToken: string }
  | { status: 'expired' }
  | { status: 'unavailable' }

/**
 * Ask the server for a new access token, and report what happened.
 *
 * A second attempt after a 401 is not superstition. The refresh cookie is
 * HttpOnly and shared by every tab, so another tab that refreshed a moment ago
 * has already replaced it in the jar: the token we sent was spent by our own
 * application, and the one the browser will send next is the successor. The
 * server distinguishes that case from a replayed token and says so. Retrying
 * once picks up the successor; retrying twice would be a loop.
 */
export async function refreshSession(): Promise<RefreshOutcome> {
  const tokenBefore = loadTokens()?.accessToken ?? null

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const tokens = await doRefresh()
      return { status: 'refreshed', accessToken: tokens.accessToken }
    } catch (err) {
      const status = (err as { status?: number }).status
      if (status !== 401 && status !== 403) {
        return { status: 'unavailable' }
      }
      if (attempt === 0) {
        // Another tab may have just rotated the cookie. Give its response time
        // to land in the jar before deciding the session is over.
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
        const stored = loadTokens()
        if (stored && stored.accessToken !== tokenBefore && stored.expiresAt > Date.now()) {
          // A DIFFERENT token is in storage now, so another tab did the work
          // while we waited. Comparing matters: the token that was already
          // there is the one that just failed.
          return { status: 'refreshed', accessToken: stored.accessToken }
        }
      }
    }
  }
  return { status: 'expired' }
}

/**
 * Drop the local session without telling the server anything.
 *
 * The distinction from `signOut()` is the point. A user pressing Sign out wants
 * the server to end the session, and it now does. A failure handler must not:
 * calling logout there deletes the refresh cookie, which is the one credential
 * that could still have recovered the session without a password and a fresh
 * authenticator code. It used to, on any 401 for any reason.
 */
export function endSessionLocally(): void {
  clearTokens()
  notify('SIGNED_OUT', null)
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Return the current access token, proactively refreshing it if it expires
 * within the next 60 seconds.
 */
export async function getAccessToken(): Promise<string | null> {
  const stored = loadTokens()
  if (!stored) return null

  const REFRESH_BUFFER_MS = 60 * 1000
  if (stored.expiresAt - Date.now() < REFRESH_BUFFER_MS) {
    const outcome = await refreshSession()
    if (outcome.status === 'refreshed') return outcome.accessToken
    if (outcome.status === 'expired') {
      endSessionLocally()
      return null
    }
    // The refresh did not come back with a verdict on the token: a rate limit, a
    // blip, a 500. The stored token is within a minute of expiry and may well
    // still work, so send it and let the request itself decide. Signing the user
    // out here was how a 429 became "your session has expired".
    return stored.accessToken
  }

  return stored.accessToken
}

export function isAuthenticated(): boolean {
  const stored = loadTokens()
  return !!stored && stored.expiresAt > Date.now()
}

/**
 * Who the stored session belongs to, answered synchronously.
 *
 * `useAuth` only knows the user after /auth/me has come back, which is one
 * round trip too late for a route guard that has to decide before the first
 * child renders. The `sub` claim of the token already in localStorage answers
 * the same question during the first render, and it is the same identity the
 * server will confirm a moment later.
 *
 * It grants nothing on its own: it is used to scope a client-side hint to the
 * right person, never to authorise anything. An unreadable or expired token
 * answers `null`, and every caller has to treat that as "ask the server".
 */
export function getStoredUserId(): string | null {
  const stored = loadTokens()
  if (!stored || stored.expiresAt <= Date.now()) return null
  return decodeSubject(stored.accessToken)
}

export async function signUp(
  email: string,
  password: string,
  options?: {
    firstName?: string
    lastName?: string
    phone?: string
    jobTitle?: string
    // GoTrue-compat wrapper — data sub-object is unwrapped automatically
    data?: { first_name?: string; last_name?: string; phone?: string; job_title?: string; full_name?: string }
    emailRedirectTo?: string
  },
): Promise<{ data: { user: AuthUser } | null; error: Error | null }> {
  try {
    // Support both the new flat shape and the GoTrue-compat nested `options.data` shape
    const firstName = options?.firstName ?? options?.data?.first_name ?? ''
    const lastName = options?.lastName ?? options?.data?.last_name
    const phone = options?.phone ?? options?.data?.phone

    const result = await post<{ accessToken: string; user: AuthUser }>(
      '/auth/register',
      { email, password, firstName, lastName, phone },
    )

    const tokens: StoredTokens = {
      accessToken: result.accessToken,
      expiresAt: decodeExp(result.accessToken),
    }
    saveTokens(tokens)

    const session: AuthSession = { ...tokens, user: result.user }
    notify('SIGNED_IN', session)

    return { data: { user: result.user }, error: null }
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error(String(err)) }
  }
}

export interface SignInResult {
  data:
    | { session: AuthSession; needs2FA?: false }
    | { session: null; needs2FA: true; twoFactorToken: string }
    | null
  error: Error | null
}

export async function signInWithPassword(
  credentials: { email: string; password: string },
): Promise<SignInResult> {
  try {
    const result = await post<
      | { accessToken: string; user: AuthUser }
      | { needs2FA: true; twoFactorToken: string }
    >('/auth/login', { email: credentials.email, password: credentials.password })

    if ('needs2FA' in result && result.needs2FA) {
      return { data: { session: null, needs2FA: true, twoFactorToken: result.twoFactorToken }, error: null }
    }

    const fullResult = result as { accessToken: string; user: AuthUser }
    const tokens: StoredTokens = {
      accessToken: fullResult.accessToken,
      expiresAt: decodeExp(fullResult.accessToken),
    }
    saveTokens(tokens)

    const session: AuthSession = { ...tokens, user: fullResult.user }
    notify('SIGNED_IN', session)

    return { data: { session }, error: null }
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error(String(err)) }
  }
}

export async function completeTwoFactorLogin(
  twoFactorToken: string,
  code: string,
): Promise<{ data: { session: AuthSession } | null; error: Error | null }> {
  try {
    const result = await post<{ accessToken: string; user: AuthUser }>(
      '/auth/2fa/login',
      { twoFactorToken, code },
    )
    const tokens: StoredTokens = {
      accessToken: result.accessToken,
      expiresAt: decodeExp(result.accessToken),
    }
    saveTokens(tokens)
    const session: AuthSession = { ...tokens, user: result.user }
    notify('SIGNED_IN', session)
    return { data: { session }, error: null }
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error(String(err)) }
  }
}

/**
 * Sign out, deliberately: the server ends the session and this ends the local
 * copy of it.
 *
 * The logout call is awaited rather than fired and forgotten. It is what ends
 * the session server-side, so an access token still in a tab somewhere stops
 * working; a request abandoned by the navigation that follows would leave that
 * token alive for the rest of its hour, which is the state this endpoint was
 * just fixed to prevent. A failure is not allowed to strand the user in a
 * half-signed-out app, so the local session is cleared either way.
 *
 * Not to be used by failure handlers. They want `endSessionLocally`, which does
 * not delete the refresh cookie.
 */
export async function signOut(): Promise<{ error: Error | null }> {
  // Server reads the rg_refresh cookie to revoke the active refresh token and
  // end its session, then clears the cookie. credentials:include ships it.
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    })
  } catch {
    // Offline, or the API is down. Nothing to do about the server's copy of the
    // session from here, and holding the user in the app would be worse.
  }
  clearTokens()
  notify('SIGNED_OUT', null)
  return { error: null }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ error: Error | null }> {
  try {
    const token = await getAccessToken()
    const res = await fetch(`${API_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to change password')
    }
    return { error: null }
  } catch (e) {
    return { error: e instanceof Error ? e : new Error(String(e)) }
  }
}

export async function getSession(): Promise<{
  data: { session: AuthSession | null }
  error: Error | null
}> {
  const stored = loadTokens()
  if (!stored) return { data: { session: null }, error: null }

  // Proactively refresh if needed
  const REFRESH_BUFFER_MS = 60 * 1000
  if (stored.expiresAt - Date.now() < REFRESH_BUFFER_MS) {
    const outcome = await refreshSession()
    if (outcome.status === 'refreshed') {
      return {
        data: {
          session: {
            accessToken: outcome.accessToken,
            expiresAt: decodeExp(outcome.accessToken),
            user: null as unknown as AuthUser,
          },
        },
        error: null,
      }
    }
    if (outcome.status === 'expired') {
      clearTokens()
      return { data: { session: null }, error: null }
    }
    // Same reasoning as getAccessToken: no verdict on the token means no grounds
    // to throw the session away. Report what is stored and let a real request
    // find out.
  }

  return {
    data: {
      session: {
        ...stored,
        user: null as unknown as AuthUser,
      },
    },
    error: null,
  }
}

export async function getUser(accessToken: string): Promise<AuthUser> {
  return get<AuthUser>('/auth/me', accessToken)
}

export async function forgotPassword(email: string): Promise<{ error: Error | null }> {
  try {
    await post('/auth/forgot-password', { email })
    return { error: null }
  } catch (err) {
    return { error: err instanceof Error ? err : new Error(String(err)) }
  }
}

/**
 * Check whether a password-reset token is still valid, so the reset page can
 * surface an "invalid/expired link" state on load instead of only on submit
 * (P6). Network/parse failures resolve to `true` so a transient blip doesn't
 * hide a genuinely valid form — the POST still re-validates server-side.
 */
export async function validateResetToken(token: string): Promise<boolean> {
  if (!token) return false
  try {
    const res = await fetch(
      `${API_URL}/auth/reset-password/validate?token=${encodeURIComponent(token)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    )
    if (!res.ok) return true
    const data = (await res.json()) as { valid?: boolean }
    return data.valid !== false
  } catch {
    return true
  }
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<{ error: Error | null }> {
  try {
    await post('/auth/reset-password', { token, newPassword })
    return { error: null }
  } catch (err) {
    return { error: err instanceof Error ? err : new Error(String(err)) }
  }
}

export function onAuthStateChange(
  callback: (event: AuthEvent, session: AuthSession | null) => void,
): { data: { subscription: { unsubscribe: () => void } } } {
  subscribers.add(callback)
  return {
    data: {
      subscription: {
        unsubscribe: () => subscribers.delete(callback),
      },
    },
  }
}

// ── GoTrue-compat default export ──────────────────────────────────────────────
// Provides the same surface area as a GoTrueClient instance so that any
// remaining `auth.xxx()` call sites don't require mass-editing.

export const auth = {
  signUp: (opts: { email: string; password: string; options?: Parameters<typeof signUp>[2] }) =>
    signUp(opts.email, opts.password, opts.options),
  signInWithPassword,
  completeTwoFactorLogin,
  signOut,
  getSession,
  onAuthStateChange,
}
